import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  MainContent,
  MixerCard,
  FormContainer,
  NetworkRow,
  ActionButton,
  Disclaimer,
  DialogOverlay,
  DialogContainer,
  DialogHeader,
  CloseButton,
  DialogContent,
  DialogSubtitle,
  TransferDetails,
  DetailCard,
  DetailLabel,
  DetailValue,
  CopyButton,
  QRCodeContainer,
  StyledQRCode,
  TimeRemaining,
  MinDepositLabel,
} from "../components/styles";
import { Navigation } from "../components/Navigation";
import { useAnimation } from "../components/AnimationContext";
import { useWebSocket } from "../components/WebSocketContext";
import { NetworkSelector } from "../components/NetworkSelector";
import { FormInput } from "../components/FormInput";
import { TransferData } from "../utils/types";
import { fetchTransferChains, convertChainToNetwork } from "../utils/api";
import { ProgressSteps } from "../components/ProgressSteps";
import { getAddress } from "ethers";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.obscurium.pro";
const X_API_KEY = process.env.REACT_APP_X_API_KEY || "35ae663864699ef4409b93ad7440890257b25b06a8299b1823dc4a31e9dccf73";

// API helper functions
const getAuthToken = (): string | null => {
  return process.env.REACT_APP_API_KEY || null;
};

export const TransferPage: React.FC = () => {
  const { disableAnimations } = useAnimation();
  const {
    isConnected,
    subscribeToTransfer,
    unsubscribeFromTransfer,
    onProgress,
    onCompleted,
    onFailed,
  } = useWebSocket();
  const location = useLocation();
  const [sourceNetwork, setSourceNetwork] = useState("ethereum");
  const [destNetwork, setDestNetwork] = useState("ethereum");
  const [amount, setAmount] = useState("");
  const [receivingAddress, setReceivingAddress] = useState("");
  const [showSourceSelector, setShowSourceSelector] = useState(false);
  const [showDestSelector, setShowDestSelector] = useState(false);
  const [amountError, setAmountError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferData, setTransferData] = useState<TransferData | null>(null);
  const [transferCopied, setTransferCopied] = useState(false);
  const [transferIdCopied, setTransferIdCopied] = useState(false);
  const [minWithdraw, setMinWithdraw] = useState<number | null>(null);
  const [minWithdrawCoin, setMinWithdrawCoin] = useState<string>("");
  const [minWithdrawLoading, setMinWithdrawLoading] = useState(false);
  const [networks, setNetworks] = useState<any[]>([]);
  const [conversionEstimate, setConversionEstimate] = useState<number | null>(
    null
  );
  const [conversionCoin, setConversionCoin] = useState<string>("");
  const [conversionLoading, setConversionLoading] = useState(false);

  // Search states
  const [sourceNetworkSearch, setSourceNetworkSearch] = useState("");
  const [destNetworkSearch, setDestNetworkSearch] = useState("");

  // Progress dialog states
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [currentTransfer, setCurrentTransfer] = useState<any>(null);
  const [progressSteps, setProgressSteps] = useState<any[]>([]);

  // Timer for countdown
  useEffect(() => {
    if (!showTransferDialog || !transferData) return;

    const timer = setInterval(() => {
      setTransferData((prev) => {
        if (!prev) return null;
        const newTime = prev.timeRemaining - 1;
        if (newTime <= 0) {
          setShowTransferDialog(false);
          return null;
        }
        return { ...prev, timeRemaining: newTime };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showTransferDialog, transferData]);

  // WebSocket subscription for real-time progress updates
  useEffect(() => {
    if (!transferData?.transferId) return;

    // Subscribe to transfer updates via WebSocket
    subscribeToTransfer(transferData.transferId);

    // Listen for progress updates
    const unsubProgress = onProgress((progress) => {
      if (progress.transferId === transferData.transferId) {
        console.log("WebSocket progress update:", progress);

        // Update progress steps
        setProgressSteps(progress.visualSteps);

        // If status changed from initiated, show progress dialog
        if (progress.status !== "initiated") {
          // Close deposit dialog and show progress dialog
          setShowTransferDialog(false);
          setCurrentTransfer({
            id: transferData.transferId,
            status: progress.status,
            origin_chain: transferData.sourceNetwork,
            destination_chain: transferData.destNetwork,
            amount: transferData.amount,
            progress: { steps: progress.visualSteps },
            binance_withdrawn_amount:
              progress.withdrawnAmount || undefined,
            current_confirms: progress.currentConfirms,
            required_confirms: progress.requiredConfirms,
            estimated_unlock_seconds: progress.estimatedUnlockSeconds,
            estimated_completion_seconds: progress.estimatedCompletionSeconds,
            total_current_confirms: progress.totalCurrentConfirms,
            total_required_confirms: progress.totalRequiredConfirms,
          });
          setShowProgressDialog(true);
        }
      }
    });

    // Listen for completion
    const unsubCompleted = onCompleted((data) => {
      if (data.transferId === transferData.transferId) {
        console.log("WebSocket transfer completed:", data);
        // Fetch updated transfer data
        fetchTransferDetails(transferData.transferId);
      }
    });

    // Listen for failures
    const unsubFailed = onFailed((data) => {
      if (data.transferId === transferData.transferId) {
        console.log("WebSocket transfer failed:", data);
        // Fetch updated transfer data
        fetchTransferDetails(transferData.transferId);
      }
    });

    return () => {
      unsubscribeFromTransfer(transferData.transferId);
      unsubProgress();
      unsubCompleted();
      unsubFailed();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transferData?.transferId,
    subscribeToTransfer,
    unsubscribeFromTransfer,
    onProgress,
    onCompleted,
    onFailed,
  ]);

  // Helper function to fetch transfer details
  const fetchTransferDetails = async (transferId: string) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/v1/transfer/status/${transferId}`,
        {
          headers: { Authorization: `Bearer ${token}`, "X-API-KEY": X_API_KEY },
        }
      );

      const data = await response.json();

      if (data.status === "success" && data.data) {
        const transfer = data.data;
        setCurrentTransfer(transfer);
        if (transfer.progress?.steps) {
          setProgressSteps(transfer.progress.steps);
        }
      }
    } catch (error) {
      console.error("Error fetching transfer details:", error);
    }
  };

  // Polling for transfer status (always poll every 30 seconds as fallback, even with WebSocket)
  useEffect(() => {
    if (!showTransferDialog || !transferData) {
      return;
    }

    // Function to fetch transfer status
    const fetchTransferStatus = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;

        const response = await fetch(
          `${API_BASE_URL}/v1/transfer/status/${transferData.transferId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-API-KEY": X_API_KEY,
            },
          }
        );

        const data = await response.json();

        if (data.status === "success" && data.data) {
          const transfer = data.data;

          // Check if status changed from initiated (Go backend uses lowercase)
          if (
            transfer.status !== "initiated" &&
            transfer.status !== "INITIATED"
          ) {
            // Close deposit dialog
            setShowTransferDialog(false);

            // Set current transfer and show progress dialog
            setCurrentTransfer(transfer);
            if (transfer.progress?.steps) {
              setProgressSteps(transfer.progress.steps);
            }
            setShowProgressDialog(true);
          }
        }
      } catch (error) {
        console.error("Error fetching transfer status:", error);
      }
    };

    // Poll every 30 seconds (WebSocket should handle faster updates when working)
    const interval = setInterval(fetchTransferStatus, 30000);

    // Also do an initial fetch after 5 seconds if WebSocket hasn't triggered
    const initialTimeout = setTimeout(fetchTransferStatus, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(initialTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTransferDialog, transferData?.transferId]);

  // Polling for progress dialog to update transfer status
  useEffect(() => {
    if (!showProgressDialog || !currentTransfer) return;

    // Get transfer ID (support both field names) - capture at start
    const transferId = currentTransfer.id || currentTransfer.transfer_id;
    if (!transferId) return;

    // Subscribe to WebSocket updates for this transfer
    subscribeToTransfer(transferId);

    const fetchUpdatedTransfer = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;

        const response = await fetch(
          `${API_BASE_URL}/v1/transfer/status/${transferId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-API-KEY": X_API_KEY,
            },
          }
        );

        const data = await response.json();

        if (data.status === "success" && data.data) {
          const transfer = data.data;
          setCurrentTransfer(transfer);
          if (transfer.progress?.steps) {
            setProgressSteps(transfer.progress.steps);
          }
        }
      } catch (error) {
        console.error("Error fetching updated transfer:", error);
      }
    };

    // Poll every 30 seconds (as fallback to WebSocket)
    const interval = setInterval(fetchUpdatedTransfer, 30000);

    // Initial fetch
    fetchUpdatedTransfer();

    return () => {
      clearInterval(interval);
      unsubscribeFromTransfer(transferId);
    };
    // Only depend on showProgressDialog - transferId is captured inside
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProgressDialog]);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowSourceSelector(false);
      setShowDestSelector(false);
      setSourceNetworkSearch("");
      setDestNetworkSearch("");
    };

    const hasOpenDropdowns = showSourceSelector || showDestSelector;

    if (hasOpenDropdowns) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showSourceSelector, showDestSelector]);

  // Fetch transfer chains on mount
  useEffect(() => {
    const loadChains = async () => {
      try {
        const chains = await fetchTransferChains();
        const convertedNetworks = chains.map(convertChainToNetwork);
        setNetworks(convertedNetworks);

        // Set default networks if not set
        if (!sourceNetwork && convertedNetworks.length > 0) {
          setSourceNetwork(convertedNetworks[0].id);
        }
        if (!destNetwork && convertedNetworks.length > 1) {
          setDestNetwork(convertedNetworks[1].id);
        }
      } catch (error) {
        console.error("Failed to load chains:", error);
      }
    };

    loadChains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch minimum withdrawal amount (with multiplier) when chains change
  useEffect(() => {
    const fetchMinWithdraw = async () => {
      if (!sourceNetwork || !destNetwork) return;

      setMinWithdrawLoading(true);
      try {
        const token = getAuthToken();
        if (!token) {
          console.error("No auth token available");
          setMinWithdrawLoading(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/v1/transfer/min-withdraw?origin_chain=${sourceNetwork}&destination_chain=${destNetwork}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-API-KEY": X_API_KEY,
            },
          }
        );

        const data = await response.json();

        if (data.status === "success") {
          // Use the minWithdrawInOriginCurrency which is in the source chain's currency
          setMinWithdraw(data.data.minWithdrawInOriginCurrency);
          setMinWithdrawCoin(data.data.originCoin);
          console.log("Min withdrawal info:", {
            baseMinWithdraw: data.data.minWithdraw,
            multiplier: data.data.multiplier,
            minWithdrawInOriginCurrency: data.data.minWithdrawInOriginCurrency,
            originCoin: data.data.originCoin,
          });
        } else {
          console.error("Failed to fetch min withdrawal:", data.message);
          setMinWithdraw(null);
          setMinWithdrawCoin("");
        }
      } catch (error) {
        console.error("Error fetching min withdrawal:", error);
        setMinWithdraw(null);
        setMinWithdrawCoin("");
      } finally {
        setMinWithdrawLoading(false);
      }
    };

    fetchMinWithdraw();
  }, [sourceNetwork, destNetwork]);

  // Fetch conversion estimate when amount or chains change
  useEffect(() => {
    const fetchConversionEstimate = async () => {
      if (!sourceNetwork || !destNetwork || !amount) {
        setConversionEstimate(null);
        setConversionCoin("");
        return;
      }

      const numericAmount = parseFloat(amount);
      if (isNaN(numericAmount) || numericAmount <= 0) {
        setConversionEstimate(null);
        setConversionCoin("");
        return;
      }

      setConversionLoading(true);
      try {
        const token = getAuthToken();
        if (!token) {
          console.error("No auth token available");
          setConversionLoading(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/v1/transfer/conversion-estimate?origin_chain=${sourceNetwork}&destination_chain=${destNetwork}&amount=${numericAmount}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-API-KEY": X_API_KEY,
            },
          }
        );

        const data = await response.json();

        if (data.status === "success") {
          setConversionEstimate(data.data.estimatedAmount);
          setConversionCoin(data.data.toCoin);
        } else {
          console.error("Failed to fetch conversion estimate:", data.message);
          setConversionEstimate(null);
          setConversionCoin("");
        }
      } catch (error) {
        console.error("Error fetching conversion estimate:", error);
        setConversionEstimate(null);
        setConversionCoin("");
      } finally {
        setConversionLoading(false);
      }
    };

    fetchConversionEstimate();
  }, [sourceNetwork, destNetwork, amount]);

  // Re-validate amount when minimum withdrawal changes
  useEffect(() => {
    if (amount && minWithdraw !== null) {
      const numericValue = parseFloat(amount);
      if (
        !isNaN(numericValue) &&
        numericValue > 0 &&
        numericValue < minWithdraw
      ) {
        setAmountError(
          `Invalid amount. Minimum is ${minWithdraw} ${minWithdrawCoin.toUpperCase()}`
        );
      } else if (
        amountError &&
        amountError.includes("Invalid amount. Minimum is")
      ) {
        // Clear the minimum amount error if the amount is now valid
        setAmountError("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minWithdraw, amount, minWithdrawCoin, amountError]);

  // Re-validate address when destination network changes
  useEffect(() => {
    if (receivingAddress) {
      const error = validateWalletAddress(receivingAddress);
      setAddressError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destNetwork, receivingAddress]);

  const validateAmount = (value: string): string => {
    if (!value.trim()) {
      return "Amount is required";
    }

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      return "Amount must be a valid number";
    }

    if (numericValue <= 0) {
      return "Amount must be greater than 0";
    }

    if (minWithdraw !== null && numericValue < minWithdraw) {
      return `Invalid amount. Minimum is ${minWithdraw} ${minWithdrawCoin.toUpperCase()}`;
    }

    if (numericValue > 1000000) {
      return "Amount is too large";
    }

    return "";
  };

  // Helper to check if a chain is Solana
  const isChainSolana = (chain: string): boolean => {
    return chain.toLowerCase() === "solana";
  };

  // Helper to get coin symbol from chain
  const getCoinFromChain = (chain: string): string => {
    const chainToCoin: Record<string, string> = {
      ethereum: "ETH",
      base: "ETH",
      arbitrum: "ETH",
      optimism: "ETH",
      bsc: "BNB",
      solana: "SOL",
    };
    return chainToCoin[chain.toLowerCase()] || "ETH";
  };

  // Helper to check if address is EVM format
  const isEvmAddress = (address: string): boolean => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  const validateWalletAddress = (address: string): string => {
    if (!address.trim()) {
      return "Wallet address is required";
    }

    // Check if it's an EVM address (Ethereum, Base, Arbitrum, Optimism, BSC, Linea)
    const isEvmAddress = /^0x[a-fA-F0-9]{40}$/.test(address);

    // Check if it's a Solana address (base58, 32-44 characters)
    const isSolanaAddress = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);

    // Validate based on destination chain
    const destinationIsSolana = isChainSolana(destNetwork);

    if (destinationIsSolana) {
      if (!isSolanaAddress) {
        return "Invalid Solana address format";
      }
    } else {
      if (!isEvmAddress) {
        return "Invalid EVM address format (must start with 0x)";
      }
    }

    return "";
  };

  const handleAmountChange = (value: string) => {
    // Only allow numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, "");

    // Prevent multiple decimal points
    const parts = numericValue.split(".");
    if (parts.length > 2) {
      return;
    }

    // Limit decimal places to 18 (standard for most tokens)
    if (parts[1] && parts[1].length > 18) {
      return;
    }

    setAmount(numericValue);

    // Clear error when user starts typing
    if (amountError) {
      setAmountError("");
    }
  };

  const handleAddressChange = (value: string) => {
    setReceivingAddress(value);

    // Clear error when user starts typing
    if (addressError) {
      setAddressError("");
    }
  };

  const handleTransfer = async () => {
    // Validate form
    const amountErr = validateAmount(amount);
    const addressErr = validateWalletAddress(receivingAddress);

    setAmountError(amountErr);
    setAddressError(addressErr);

    if (amountErr || addressErr) {
      console.log("❌ Transfer validation failed:", {
        amountError: amountErr,
        addressError: addressErr,
      });
      return;
    }

    setTransferLoading(true);

    try {
      const token = getAuthToken();
      if (!token) {
        setAmountError("Authentication failed");
        setTransferLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/v1/transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-API-KEY": X_API_KEY,
        },
        body: JSON.stringify({
          origin_chain: sourceNetwork,
          destination_chain: destNetwork,
          amount,
          receiving_wallet_address: receivingAddress,
        }),
      });

      const data = await response.json();

      if (data.status === "success") {
        // Set transfer data and show dialog
        setTransferData({
          transferId: data.data.transfer_id,
          sourceNetwork,
          destNetwork,
          amount,
          depositAddress: data.data.deposit_wallet_address,
          timeRemaining: 15 * 60, // 15 minutes in seconds
        });
        setShowTransferDialog(true);

        console.log("✅ Transfer initiated successfully!", {
          transferId: data.data.transfer_id,
          depositAddress: data.data.deposit_wallet_address,
        });
      } else {
        setAmountError(data.message || "Transfer failed");
      }
    } catch (error) {
      console.error("Transfer error", error);
      setAmountError("Network error");
    } finally {
      setTransferLoading(false);
    }
  };

  // Copy to clipboard function
  const copyToClipboard = async (
    text: string,
    setCopied: (value: boolean) => void
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const getNetworkById = (id: string) => networks.find((n) => n.id === id);

  // Handler for source network changes - syncs destination if on /transfer path
  const handleSourceNetworkChange = (networkId: string) => {
    setSourceNetwork(networkId);
    // If on /transfer path, automatically set destination to the same chain
    if (location.pathname === "/transfer") {
      setDestNetwork(networkId);
    }
  };

  // Handler for destination network changes - syncs source if on /transfer path
  const handleDestNetworkChange = (networkId: string) => {
    setDestNetwork(networkId);
    // If on /transfer path, automatically set source to the same chain
    if (location.pathname === "/transfer") {
      setSourceNetwork(networkId);
    }
  };

  return (
    <>
      <MainContent>
        <MixerCard $disableAnimation={disableAnimations}>
          <Navigation />
          <FormContainer $disableAnimation={disableAnimations}>
            <NetworkRow>
              <NetworkSelector
                selectedId={sourceNetwork}
                onSelect={handleSourceNetworkChange}
                isOpen={showSourceSelector}
                onToggle={() => setShowSourceSelector(!showSourceSelector)}
                label="Source Network"
                search={sourceNetworkSearch}
                onSearchChange={setSourceNetworkSearch}
                networks={networks}
              />
              {location.pathname === "/swap" && (
                <NetworkSelector
                  selectedId={destNetwork}
                  onSelect={handleDestNetworkChange}
                  isOpen={showDestSelector}
                  onToggle={() => setShowDestSelector(!showDestSelector)}
                  label="Destination Network"
                  search={destNetworkSearch}
                  onSearchChange={setDestNetworkSearch}
                  networks={networks}
                />
              )}
            </NetworkRow>

            <FormInput
              label="Amount to Transfer"
              type="text"
              placeholder="0.00"
              value={amount}
              onChange={handleAmountChange}
              hasError={!!amountError}
              errorMessage={amountError}
            />

            {minWithdraw !== null && minWithdrawCoin && (
              <MinDepositLabel>
                Minimum amount: {minWithdraw.toFixed(6)}{" "}
                {minWithdrawCoin.toUpperCase()}
              </MinDepositLabel>
            )}
            {minWithdrawLoading && (
              <MinDepositLabel>Loading minimum amount...</MinDepositLabel>
            )}

            {conversionEstimate !== null &&
              conversionCoin &&
              amount &&
              parseFloat(amount) > 0 && (
                <MinDepositLabel
                  style={{ color: "#10b981", marginTop: "0.1rem" }}
                >
                  You will receive approximately:{" "}
                  {conversionEstimate.toFixed(6)} {conversionCoin.toUpperCase()}
                </MinDepositLabel>
              )}
            {conversionLoading && (
              <MinDepositLabel style={{ marginTop: "0.5rem" }}>
                Calculating conversion...
              </MinDepositLabel>
            )}

            <FormInput
              label="Receiving Wallet Address"
              type="text"
              placeholder={
                isChainSolana(destNetwork)
                  ? "Enter Solana address"
                  : "Enter EVM address (0x...)"
              }
              value={receivingAddress}
              onChange={handleAddressChange}
              hasError={!!addressError}
              errorMessage={addressError}
            />

            <ActionButton
              onClick={handleTransfer}
              disabled={transferLoading}
              $loading={transferLoading}
            >
              {transferLoading ? "Processing..." : "Obscure Transfer"}
            </ActionButton>
          </FormContainer>

          <Disclaimer>
            Obscurium provides enhanced privacy for your transactions. Please
            ensure compliance with local regulations.
          </Disclaimer>
        </MixerCard>
      </MainContent>

      {/* Transfer Dialog */}
      {showTransferDialog && transferData && (
        <DialogOverlay
          $disableAnimation={disableAnimations}
          onClick={() => setShowTransferDialog(false)}
        >
          <DialogContainer
            $disableAnimation={disableAnimations}
            onClick={(e) => e.stopPropagation()}
          >
            <DialogHeader>
              <CloseButton onClick={() => setShowTransferDialog(false)}>
                ×
              </CloseButton>
            </DialogHeader>

            <DialogContent>
              <DialogSubtitle>
                Please send the amount below to the provided address
              </DialogSubtitle>

              <TransferDetails>
                <DetailCard>
                  <DetailLabel>Transfer ID</DetailLabel>
                  <DetailValue
                    style={{ fontSize: "0.75rem", fontFamily: "monospace" }}
                  >
                    {transferData.transferId}
                    <CopyButton
                      $copied={transferIdCopied}
                      onClick={() =>
                        copyToClipboard(
                          transferData.transferId,
                          setTransferIdCopied
                        )
                      }
                    >
                      {transferIdCopied ? "Copied!" : "Copy"}
                    </CopyButton>
                  </DetailValue>
                </DetailCard>

                <DetailCard>
                  <DetailLabel>Source Network</DetailLabel>
                  <DetailValue>
                    {getNetworkById(transferData.sourceNetwork)?.name}
                  </DetailValue>
                </DetailCard>

                <DetailCard>
                  <DetailLabel>Destination Network</DetailLabel>
                  <DetailValue>
                    {getNetworkById(transferData.destNetwork)?.name}
                  </DetailValue>
                </DetailCard>

                <DetailCard>
                  <DetailLabel>Amount to Transfer</DetailLabel>
                  <DetailValue>
                    {transferData.amount}{" "}
                    {getCoinFromChain(transferData.sourceNetwork)}
                  </DetailValue>
                </DetailCard>

                <DetailCard>
                  <DetailLabel>Deposit Address</DetailLabel>
                  <QRCodeContainer>
                    <StyledQRCode
                      value={transferData.depositAddress}
                      size={150}
                      bgColor="white"
                      fgColor="#000000"
                      level="M"
                    />
                  </QRCodeContainer>
                  <DetailValue>
                    {transferData.depositAddress}
                    <CopyButton
                      $copied={transferCopied}
                      onClick={() =>
                        copyToClipboard(
                          transferData.depositAddress,
                          setTransferCopied
                        )
                      }
                    >
                      {transferCopied ? "Copied!" : "Copy"}
                    </CopyButton>
                  </DetailValue>
                </DetailCard>
              </TransferDetails>

              <TimeRemaining>
                Time remaining:{" "}
                {formatTimeRemaining(transferData.timeRemaining)}
              </TimeRemaining>
            </DialogContent>
          </DialogContainer>
        </DialogOverlay>
      )}

      {/* Progress Dialog */}
      {showProgressDialog && currentTransfer && (
        <DialogOverlay
          $disableAnimation={disableAnimations}
          onClick={() => setShowProgressDialog(false)}
        >
          <DialogContainer
            $disableAnimation={disableAnimations}
            onClick={(e) => e.stopPropagation()}
          >
            <DialogHeader>
              <CloseButton onClick={() => setShowProgressDialog(false)}>
                ×
              </CloseButton>
            </DialogHeader>

            <DialogContent>
              <TransferDetails>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <div>
                    <DetailLabel>Transfer ID</DetailLabel>
                    <DetailValue
                      style={{ fontSize: "0.75rem", fontFamily: "monospace" }}
                    >
                      {currentTransfer.id || currentTransfer.transfer_id}
                    </DetailValue>
                  </div>
                  <div>
                    <DetailLabel>Status</DetailLabel>
                    <DetailValue
                      style={{
                        color:
                          currentTransfer.status === "completed"
                            ? "#10b981"
                            : currentTransfer.status === "failed"
                              ? "#ef4444"
                              : "#f59e0b",
                        fontWeight: 600,
                      }}
                    >
                      {currentTransfer.status?.toUpperCase()}
                    </DetailValue>
                  </div>
                  <div>
                    <DetailLabel>From</DetailLabel>
                    <DetailValue>
                      {currentTransfer.origin_chain ||
                        currentTransfer.from_chain}
                      : {currentTransfer.amount}{" "}
                      {getCoinFromChain(
                        currentTransfer.origin_chain ||
                        currentTransfer.from_chain ||
                        ""
                      )}
                    </DetailValue>
                  </div>
                  <div>
                    <DetailLabel>To (Estimated)</DetailLabel>
                    <DetailValue>
                      {currentTransfer.binance_withdrawn_amount
                        ? `${currentTransfer.destination_chain ||
                        currentTransfer.to_chain
                        }: ${parseFloat(
                          currentTransfer.binance_withdrawn_amount
                        ).toFixed(6)} ${getCoinFromChain(
                          currentTransfer.destination_chain ||
                          currentTransfer.to_chain ||
                          ""
                        )}`
                        : `${currentTransfer.destination_chain ||
                        currentTransfer.to_chain
                        }: Calculating...`}
                    </DetailValue>
                  </div>
                </div>

                {/* Addresses Row - Side by Side */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <div>
                    <DetailLabel>Sender Address</DetailLabel>
                    <DetailValue
                      style={{ fontSize: "0.65rem", fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      {currentTransfer.sender_address &&
                        isEvmAddress(currentTransfer.sender_address)
                        ? getAddress(currentTransfer.sender_address)
                        : currentTransfer.sender_address || "Pending..."}
                    </DetailValue>
                  </div>
                  <div>
                    <DetailLabel>Receiving Address</DetailLabel>
                    <DetailValue
                      style={{ fontSize: "0.65rem", fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      {(currentTransfer.receiving_wallet_address ||
                        currentTransfer.receiving_address) &&
                        isEvmAddress(
                          currentTransfer.receiving_wallet_address ||
                          currentTransfer.receiving_address ||
                          ""
                        )
                        ? getAddress(
                          currentTransfer.receiving_wallet_address ||
                          currentTransfer.receiving_address
                        )
                        : currentTransfer.receiving_wallet_address ||
                        currentTransfer.receiving_address}
                    </DetailValue>
                  </div>
                </div>

                {/* Estimated Time Section - Always show for active transfers */}
                {currentTransfer.status !== "completed" &&
                  currentTransfer.status !== "failed" && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "0.75rem",
                        backgroundColor: "rgba(16, 185, 129, 0.08)",
                        borderRadius: "0.5rem",
                        border: "1px solid rgba(16, 185, 129, 0.2)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                        }}
                      >
                        <div>
                          <DetailLabel style={{ color: "#10b981" }}>
                            Estimated Time to Complete
                          </DetailLabel>
                          <DetailValue
                            style={{
                              color: "#10b981",
                              fontWeight: 600,
                              fontSize: "1.1rem",
                            }}
                          >
                            {currentTransfer.estimated_completion_seconds &&
                              currentTransfer.estimated_completion_seconds > 0
                              ? currentTransfer.estimated_completion_seconds >= 3600
                                ? `~${Math.floor(currentTransfer.estimated_completion_seconds / 3600)}h ${Math.floor((currentTransfer.estimated_completion_seconds % 3600) / 60)}m`
                                : `~${Math.ceil(currentTransfer.estimated_completion_seconds / 60)} min`
                              : "Calculating..."}
                          </DetailValue>
                        </div>
                        {((currentTransfer.total_current_confirms !== undefined &&
                          currentTransfer.total_required_confirms !== undefined &&
                          currentTransfer.total_required_confirms > 0) ||
                          (currentTransfer.current_confirms !== undefined &&
                            currentTransfer.required_confirms !== undefined &&
                            currentTransfer.required_confirms > 0)) && (
                            <div style={{ textAlign: "right" }}>
                              <DetailLabel style={{ color: "rgba(255, 255, 255, 0.6)" }}>
                                Confirmations
                              </DetailLabel>
                              <DetailValue
                                style={{
                                  fontSize: "0.9rem",
                                  color: "rgba(255, 255, 255, 0.8)",
                                }}
                              >
                                {currentTransfer.total_current_confirms !== undefined &&
                                  currentTransfer.total_required_confirms !== undefined
                                  ? `${currentTransfer.total_current_confirms}/${currentTransfer.total_required_confirms}`
                                  : `${currentTransfer.current_confirms}/${currentTransfer.required_confirms}`}
                              </DetailValue>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                {currentTransfer.status === "failed" &&
                  currentTransfer.failure_reason && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "0.75rem",
                        backgroundColor: "rgba(239, 68, 68, 0.1)",
                        borderRadius: "0.5rem",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                      }}
                    >
                      <DetailLabel style={{ color: "#ef4444" }}>
                        Failure Reason
                      </DetailLabel>
                      <DetailValue
                        style={{
                          color: "#ef4444",
                          fontWeight: 500,
                          textTransform: "capitalize",
                        }}
                      >
                        {currentTransfer.failure_reason === "expired"
                          ? "Deposit not detected after 15 minutes"
                          : currentTransfer.failure_reason}
                      </DetailValue>
                    </div>
                  )}

                <div style={{ marginTop: "1rem" }}>
                  <DetailLabel>Creation date</DetailLabel>
                  <DetailValue>
                    {currentTransfer.created_at
                      ? new Date(currentTransfer.created_at).toLocaleString()
                      : currentTransfer.timestamp}
                  </DetailValue>
                </div>

                {/* Visual Progress Steps */}
                {(progressSteps.length > 0 ||
                  (currentTransfer.progress &&
                    currentTransfer.progress.steps)) && (
                    <ProgressSteps
                      steps={
                        progressSteps.length > 0
                          ? progressSteps
                          : currentTransfer.progress.steps
                      }
                    />
                  )}
              </TransferDetails>
            </DialogContent>
          </DialogContainer>
        </DialogOverlay>
      )}
    </>
  );
};
